import { useState, useEffect } from 'react';
import { type IncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { generatePaymentSpec } from '@tectonics/prs/payment-spec/generator';
import { getLookAndFeelFromEnvironment } from '@tectonics/controller/get-look-and-feel-from-environment';
import { loadCountrySpecGenerator } from './load-country';

const DEFAULT_LOCAL_TENANCY = 'CROSS_BORDER';

export function useDynamicPaymentSpec(incomingPayment?: IncomingPayment, localTenancy?: string): [PaymentSpec | null, Error | null] {
  const [spec, setSpec] = useState<PaymentSpec | null>(null);
  const [loadingSpecError, setLoadingSpecError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPaymentSpec() {
      if (!incomingPayment) return;

      setLoadingSpecError(null);

      try {
        const generator = await loadCountrySpecGenerator(getCountry(incomingPayment));

        if (cancelled) return;

        setSpec(
          generatePaymentSpec({
            incomingPayment,
            localTenancy: localTenancy || DEFAULT_LOCAL_TENANCY,
            generator,
            lookAndFeelCode: getLookAndFeelFromEnvironment(incomingPayment).code,
          })
        );
      } catch (e) {
        if (cancelled) return;
        setLoadingSpecError(e instanceof Error ? e : new Error(String(e)));
        setSpec(null);
      }
    }

    loadPaymentSpec();

    return () => { cancelled = true; };
  }, [incomingPayment]);

  return [spec, loadingSpecError];
}
